// ──────────────────────────────────────────────────────────────────
// Global AI Agent briefing templates.
//
// Each template fills the four briefing fields (who/goal/context/
// instructions) on the AI Agent screen, and optionally pre-fills the
// dial-screen phone number if the template carries one.
//
// Schema: {
//   id:           stable string ID, used as React key
//   name:         display label on template card
//   icon:         single emoji rendered in the card
//   category:     "Healthcare" | "Everyday"  (for modal grouping)
//   phone:        digits-only string, "" if none
//   who:          "WHO" field
//   goal:         "GOAL" field
//   context:      "CONTEXT" field — uses [BRACKETS] as user-fill prompts
//   instructions: "INSTRUCTIONS" field
// }
//
// Bracketed placeholders like [Patient name], [Rx number], [Account #]
// are intentional — they signal to the user "edit me before calling."
// They're not template variables; they stay literal in the briefing
// until the user replaces them.
//
// Per-user templates (saved in Supabase) will use the same schema with
// an extra `userId` column. Adding `category: "Saved"` will be how we
// surface them in the picker once that lands.
// ──────────────────────────────────────────────────────────────────

export const GLOBAL_TEMPLATES = [
  // ── Healthcare ──────────────────────────────────────────────────
  {
    id: "doctors-office",
    name: "Doctor's Office",
    icon: "🩺",
    category: "Healthcare",
    phone: "",
    who: "Doctor's office",
    goal: "Schedule, reschedule, or cancel an appointment",
    context: "Patient: [Your name]. DOB: [MM/DD/YYYY]. Reason for visit: [reason].",
    instructions: "Offer 2-3 alternative time windows if first choice unavailable. Do not leave a voicemail — request a callback instead. Confirm appointment date, time, and provider name before ending.",
  },
  {
    id: "pharmacy-refill",
    name: "Pharmacy Refill",
    icon: "💊",
    category: "Healthcare",
    phone: "",
    who: "Pharmacy",
    goal: "Refill a prescription",
    context: "Patient: [Your name]. DOB: [MM/DD/YYYY]. Prescription: [medication name]. Rx number: [Rx #]. Pickup or delivery: [pickup/delivery].",
    instructions: "Confirm pickup time. Ask if insurance copay has changed. If the prescription requires doctor authorization, ask the pharmacy to fax the request and tell us when to expect the refill.",
  },
  {
    id: "insurance-inquiry",
    name: "Insurance Inquiry",
    icon: "🛡️",
    category: "Healthcare",
    phone: "",
    who: "Insurance company",
    goal: "Ask about coverage, claim status, or benefits",
    context: "Member: [Your name]. DOB: [MM/DD/YYYY]. Member ID: [ID]. Policy: [policy #]. Specific question: [what you want to know].",
    instructions: "Get the representative's name and a reference number for the call. Ask for written confirmation by email or mail. If the answer is 'we'll get back to you,' get a specific timeline.",
  },

  // ── Everyday ────────────────────────────────────────────────────
  {
    id: "restaurant-reservation",
    name: "Restaurant Reservation",
    icon: "🍽️",
    category: "Everyday",
    phone: "",
    who: "Restaurant",
    goal: "Book a reservation",
    context: "Name: [Your name]. Party size: [number]. Date and time: [date, time]. Special requests: [allergies, accessibility, occasion, seating preference].",
    instructions: "If the requested time is unavailable, accept anything within 30 minutes earlier or later. Confirm the reservation name and any confirmation number before ending.",
  },
  {
    id: "customer-service",
    name: "Customer Service",
    icon: "📞",
    category: "Everyday",
    phone: "",
    who: "Company customer service",
    goal: "Resolve an issue with a recent purchase, account, or service",
    context: "Account: [name on account]. Account number: [account #]. Issue: [brief description]. What you've already tried: [if anything].",
    instructions: "Stay polite but firm. If the first-line representative cannot resolve it, request escalation to a supervisor. Always get a ticket or reference number before ending the call.",
  },
  {
    id: "service-appointment",
    name: "Service Appointment",
    icon: "🔧",
    category: "Everyday",
    phone: "",
    who: "Service provider (HVAC, plumber, electrician, etc.)",
    goal: "Schedule a service visit",
    context: "Address: [your address]. Issue: [brief description of problem]. Preferred time windows: [days/times that work]. Urgency: [emergency / this week / flexible].",
    instructions: "Confirm the technician's arrival window and a cost estimate or service-call fee. Ask about cancellation and rescheduling policies. Get the technician's name if possible.",
  },
  {
    id: "order-status",
    name: "Order Status",
    icon: "📦",
    category: "Everyday",
    phone: "",
    who: "Business or store",
    goal: "Check status of an order",
    context: "Name: [Your name]. Order number: [order #]. Expected delivery date: [original date].",
    instructions: "Get tracking information if the order has shipped. If delayed, get an updated ETA in writing (email confirmation). If significantly late, ask about partial refund or compensation options.",
  },
  {
    id: "isp-support",
    name: "Cable / Internet",
    icon: "📡",
    category: "Everyday",
    phone: "",
    who: "Internet or cable company",
    goal: "Troubleshoot service issue or schedule a technician",
    context: "Account: [name on account]. Account number: [account #]. Service address: [address]. When issue started: [date/time]. What you've already tried: [reboot router, etc].",
    instructions: "If remote troubleshooting fails, schedule a technician visit. Get the appointment window confirmed by email or text. Ask if any service credit applies for the outage period.",
  },
];

// Group templates by category for the picker modal.
export const TEMPLATE_CATEGORIES = ["Healthcare", "Everyday"];

export const groupTemplatesByCategory = (templates) =>
  TEMPLATE_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = templates.filter((t) => t.category === cat);
    return acc;
  }, {});
